import { Link } from 'react-router-dom';
import Reveal from '@/components/Reveal';
import Img from '@/components/Img';
import Seo from '@/components/Seo';
import { STORY_PAGE } from '@/constants/testIds';
import { PROCESS_STEPS, SOCIAL } from '@/constants/site';

const FOUNDER_GILL = '/assets/founder-gill.jpg';
const FOUNDER_VANDANA = '/assets/founder-vandana.jpg';
const ATELIER = '/assets/image-1.jpg';

const INSTA_PLACEHOLDERS = [
  '/assets/w-lehenga-maroon.jpg',
  '/assets/w-suite.jpg',
  '/assets/w-indowestern.jpg',
  '/assets/w-chainprint.jpg',
];

export default function OurStory() {
  return (
    <div data-testid={STORY_PAGE.root} className="bg-[hsl(var(--bb-ivory))]">
      <Seo
        title="Our Story — Mr. Hardeep Singh Gill & Mrs. Vandana | Bombay Bespoke"
        description="Three decades of bespoke craftsmanship in Dubai. The story of Bombay Bespoke Fashion House — Mr. Hardeep Singh Gill and Mrs. Vandana — and the six-step process behind every garment we make."
        canonical="https://bombaybespokefashion.com/our-story"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="bb-container pt-14 md:pt-24 pb-12 md:pb-20 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="bb-eyebrow">Our Story · Dubai</div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="bb-display text-[58px] sm:text-[80px] lg:text-[112px] mt-5 leading-[0.92] text-[hsl(var(--bb-text))]">
                A house built on
                <br />
                <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">three decades</span>
                <br />
                of practice.
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed">
                Bombay Bespoke Fashion House stands as a beacon of elegance and sophistication
                in the heart of Dubai, founded by Mr. Hardeep Singh Gill. With an impressive
                thirty years in the fashion and tailoring industry, he has dedicated his
                career to perfecting the art of bespoke tailoring — blending traditional
                craftsmanship with contemporary style. The women’s atelier, VM by Vandana,
                is led by his partner, Mrs. Vandana, whose practice in design and couture
                shapes every silhouette that leaves the house.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={ATELIER} alt="The atelier" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOUNDER BIOS — alternating editorial */}
      <section className="bb-container py-16 md:py-24">
        <div className="grid grid-cols-12 gap-10 items-center" data-testid={STORY_PAGE.founderGill}>
          <div className="col-span-12 lg:col-span-5">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={FOUNDER_GILL} alt="Mr. Hardeep Singh Gill" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-10">
            <Reveal>
              <div className="bb-eyebrow">The Founder · Bombay Bespoke</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="bb-display text-4xl md:text-6xl mt-4 text-[hsl(var(--bb-text))]">
                Mr. Hardeep <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">Singh Gill</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed max-w-2xl">
                Mr. Gill’s journey in tailoring began three decades ago, marked by a
                relentless pursuit of excellence and a passion for fashion. His expertise
                spans every facet of the industry — from cloth selection and pattern
                drafting to the intricate details of stitching and fitting. Under his
                leadership, Bombay Bespoke has grown into a trusted house, known for its
                commitment to quality, innovation, and the satisfaction of every client.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid grid-cols-12 gap-10 items-center" data-testid={STORY_PAGE.founderVandana}>
          <div className="col-span-12 lg:col-span-5 lg:order-2">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={FOUNDER_VANDANA} alt="Mrs. Vandana" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:order-1 lg:pr-10">
            <Reveal>
              <div className="bb-eyebrow-emerald">The Designer · VM by Vandana</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="bb-display text-4xl md:text-6xl mt-4 text-[hsl(var(--bb-text))]">
                Mrs. <span className="bb-italic text-[hsl(var(--bb-emerald))]">Vandana</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed max-w-2xl">
                A designer who draws from heritage and dresses for the present. Mrs.
                Vandana leads the women’s line — lehengas, gowns, saris, resort wear and
                bespoke couture — meeting each woman in conversation, sketching her
                silhouette, and shepherding the garment through the atelier from first
                fitting to final hem.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW WE WORK — vertical timeline */}
      <section className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-28 overflow-hidden bb-grain">
        <div className="relative z-10 bb-container">
          <Reveal>
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">The Process</div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="bb-display text-5xl md:text-6xl mt-3 max-w-3xl">
              How we <span className="bb-italic text-[hsl(var(--bb-gold))]">work.</span>
            </h2>
          </Reveal>

          <div className="mt-14 md:mt-20 relative">
            <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-[hsl(var(--bb-gold))]/35" />
            <div className="space-y-12 md:space-y-20">
              {PROCESS_STEPS.map((step, i) => {
                const right = i % 2 === 1;
                return (
                  <Reveal
                    key={step.n}
                    delay={i * 60}
                    data-testid={STORY_PAGE.howWeWorkStep(step.n)}
                    className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-start ${
                      right ? '' : ''
                    }`}
                  >
                    <span className="absolute left-3 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[hsl(var(--bb-gold))]" />
                    <div className={`${right ? 'md:order-2 md:pl-10' : 'md:order-1 md:pr-10 md:text-right'}`}>
                      <div className="bb-display text-5xl md:text-6xl text-[hsl(var(--bb-gold))]/85">{step.n}</div>
                      <div className="bb-display text-3xl md:text-4xl mt-2">{step.title}</div>
                    </div>
                    <div className={`mt-3 md:mt-2 max-w-md ${right ? 'md:order-1 md:pr-10 md:text-right md:ml-auto' : 'md:order-2 md:pl-10'}`}>
                      <p className="text-[hsl(var(--bb-text))]/72 leading-relaxed">{step.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <Reveal className="mt-20 text-center">
            <Link
              to="/book-appointment"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
            >
              Begin your bespoke journey
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM PLACEHOLDER */}
      <section className="bb-container py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <Reveal>
            <h2 className="bb-display text-4xl md:text-5xl text-[hsl(var(--bb-text))]">
              From the <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">atelier</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noreferrer"
              className="bb-link bb-link-reverse text-[hsl(var(--bb-text))]"
            >
              Follow @vmandbb →
            </a>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {INSTA_PLACEHOLDERS.map((src, i) => (
            <Reveal key={i} delay={i * 80} className="bb-image-hover relative aspect-square overflow-hidden">
              <Img src={src} alt="" imgClassName="w-full h-full object-cover" />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}